import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from './ObjectManagerPanel.module.css';
import { SearchIcon, CloseIcon, BoundaryIcon, BuildingIcon, RoadIcon, PinIcon, FilterIcon, PlusIcon, MinusIcon } from '../Icon';
import ObjectFilterPanel from '../ObjectFilterPanel/ObjectFilterPanel';

const ObjectManagerPanel = ({ isVisible, onClose, mapData, onFeatureSelect, focusedFeatureId, onVisibilityChange, legendData, onBulkVisibilityChange }) => {
    const [advancedFilters, setAdvancedFilters] = useState({});
    const listRef = useRef(null);
    const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);

    const defaultAdvancedFilters = {
        searchTerm: '',
        activeTypes: ['plot', 'building', 'line', 'marker'],
        minArea: null,
        maxArea: null,
        ownerSearch: '',
        minHeight: null,
        maxHeight: null,
        hasTenants: false,
        hasMedia: false,
        hasDescription: false,
        activeStatuses: [], // По умолчанию статусы не активны
        noCategory: false, // Фильтр для объектов без категории
        activeCategories: [], // Фильтр по категориям
    };

    // Функция для проверки, активен ли расширенный фильтр
    const isAdvancedFilterActive = useMemo(() => {
        if (Object.keys(advancedFilters).length === 0) return false;

        // Сравниваем с дефолтными значениями. Фильтр активен, если хоть одно значение отличается.
        for (const key in advancedFilters) {
            // Сравнение массивов
            if (Array.isArray(advancedFilters[key])) {
                // Для activeTypes и activeCategories пустой массив - неактивное состояние, а для activeStatuses - активное (т.к. по умолчанию они пусты)
                const defaultArray = defaultAdvancedFilters[key] || [];
                if (JSON.stringify(advancedFilters[key].sort()) !== JSON.stringify(defaultArray.sort())) return true;
            }
            if (!Array.isArray(advancedFilters[key]) && advancedFilters[key] !== defaultAdvancedFilters[key]) return true;
        }
        return false;
    }, [advancedFilters, defaultAdvancedFilters]);

    // Восстановление состояния при монтировании
    useEffect(() => {
        const savedState = sessionStorage.getItem('objectManagerState');
        if (savedState) {
            const { filters, scroll } = JSON.parse(savedState);
            setAdvancedFilters(filters || {});
            if (listRef.current) {
                listRef.current.scrollTop = scroll;
            }
        }
    }, []);

    // Сохранение состояния при изменении
    useEffect(() => {
        const state = {
            filters: advancedFilters,
            scroll: listRef.current ? listRef.current.scrollTop : 0,
        };
        sessionStorage.setItem('objectManagerState', JSON.stringify(state));
    }, [advancedFilters]);

    // Сохранение позиции скролла
    useEffect(() => {
        const listEl = listRef.current;
        const saveScroll = () => {
            const state = JSON.parse(sessionStorage.getItem('objectManagerState') || '{}');
            state.scroll = listEl.scrollTop;
            sessionStorage.setItem('objectManagerState', JSON.stringify(state));
        };
        listEl?.addEventListener('scroll', saveScroll);
        return () => listEl?.removeEventListener('scroll', saveScroll);
    }, []);

    // Прокрутка к выделенному элементу
    useEffect(() => {
        if (focusedFeatureId && listRef.current) {
            const itemElement = listRef.current.querySelector(`[data-id="${focusedFeatureId}"]`);
            if (itemElement) {
                itemElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        }
    }, [focusedFeatureId]);


    const handleApplyAdvancedFilters = (filters) => {
        // Если фильтры пустые (сброшены), используем дефолтные
        const isReset = Object.keys(filters).length === 0;
        if (isReset) {
            setAdvancedFilters({}); // Сбрасываем расширенные фильтры
            setIsFilterPanelVisible(false); // Закрываем панель фильтров
        } else {
            setAdvancedFilters(filters);
        }
    };

    const handleBulkVisibility = (isVisible) => {
        if (filteredFeatures.length > 0 && onBulkVisibilityChange) {
            const ids = filteredFeatures.map(f => f.id);
            onBulkVisibilityChange(ids, isVisible);
        }
    };

    const filteredFeatures = useMemo(() => {
        if (!mapData?.features) return [];

        const isAdvFilter = isAdvancedFilterActive;
        const currentSearchTerm = advancedFilters?.searchTerm || '';
        const currentActiveTypes = advancedFilters.activeTypes || defaultAdvancedFilters.activeTypes;

        const typesToFilter = currentActiveTypes.includes('line')
            ? [...currentActiveTypes.filter(f => f !== 'line'), 'road', 'line']
            : currentActiveTypes;

        return mapData.features
            .filter(f => typesToFilter.includes(f.properties.type))
            .filter(f => {
                const term = currentSearchTerm.toLowerCase();
                if (!term) return true;
                const props = f.properties;
                return (
                    props.name?.toLowerCase().includes(term) ||
                    props.cadastral_number?.toLowerCase().includes(term) ||
                    f.id.toLowerCase().includes(term) ||
                    props.description?.toLowerCase().includes(term)
                );
            })
            .filter(f => {
                if (!isAdvFilter) return true;
                const props = f.properties || {};
                const { minArea, maxArea, ownerSearch, minHeight, maxHeight, hasTenants, hasMedia, hasDescription, activeStatuses, activeCategories, noCategory } = advancedFilters;

                const area = props.area;
                const height = props.height;

                const areaCheck = (minArea === undefined || minArea === null || minArea === '' || area === undefined || area >= parseFloat(minArea)) &&
                                  (maxArea === undefined || maxArea === null || maxArea === '' || area === undefined || area <= parseFloat(maxArea));

                const heightCheck = (minHeight === undefined || minHeight === null || minHeight === '' || height === undefined || height >= parseFloat(minHeight)) &&
                                    (maxHeight === undefined || maxHeight === null || maxHeight === '' || height === undefined || height <= parseFloat(maxHeight));

                const ownerCheck = !ownerSearch || props.owner?.toLowerCase().includes(ownerSearch.toLowerCase());

                const tenantsCheck = !hasTenants || (Array.isArray(props.tenants) && props.tenants.length > 0);
                const mediaCheck = !hasMedia || ((Array.isArray(props.photos) && props.photos.length > 0) || (Array.isArray(props.links) && props.links.length > 0));
                const descriptionCheck = !hasDescription || (props.description && props.description.trim() !== '');

                // Если массив статусов пуст, фильтр по статусам не применяется. Если не пуст - ищем совпадения.
                const statusCheck = !activeStatuses || activeStatuses.length === 0 || activeStatuses.some(status => props[status] === true);
                
                // Новая логика проверки категорий
                const hasActiveCategoryFilter = activeCategories && activeCategories.length > 0;
                const hasNoCategoryFilter = noCategory === true;

                let categoryCheck = true; // По умолчанию все проходят
                if (hasActiveCategoryFilter || hasNoCategoryFilter) {
                    const inActiveCategories = hasActiveCategoryFilter && activeCategories.includes(props.category);
                    const isUncategorized = hasNoCategoryFilter && (props.category === null || props.category === undefined || props.category === '');
                    categoryCheck = inActiveCategories || isUncategorized;
                }
                return areaCheck && heightCheck && ownerCheck && tenantsCheck && mediaCheck && descriptionCheck && statusCheck && categoryCheck;
            })
            .sort((a, b) => {
                const dateA = a.properties.created_at;
                const dateB = b.properties.created_at;

                // Сортируем по дате создания, самые новые вверху
                if (dateA && dateB) {
                    return dateB.localeCompare(dateA);
                }
                // Если у одного из объектов нет даты, он идет после того, у которого есть
                if (dateB) return 1;
                if (dateA) return -1;
                // Если дат нет у обоих, сортируем по имени
                return (a.properties.name || '').localeCompare(b.properties.name || '');
            });
    }, [mapData, advancedFilters, isAdvancedFilterActive]);

    const getIcon = (type) => {
        switch (type) {
            case 'plot': return <BoundaryIcon size={16} />;
            case 'building': return <BuildingIcon size={16} />;
            case 'road': case 'line': return <RoadIcon size={16} />;
            case 'marker': return <PinIcon size={16} />;
            default: return null;
        }
    };

    if (!isVisible) return null;

    return (
        <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.header}>
                <h3 style={{ flexGrow: 1 }}>Менеджер объектов</h3>
                <button onClick={onClose} className={styles.closeButton}><CloseIcon size={20} /></button>
                <button
                    onClick={() => setIsFilterPanelVisible(prev => !prev)}
                    className={`${styles.filterPanelButton} ${isAdvancedFilterActive ? styles.active : ''}`}
                    title="Расширенный фильтр"
                >
                    <FilterIcon size={20} />
                </button>
            </div>
            <div className={styles.searchContainer}>
                <SearchIcon className={styles.searchIcon} />
                <input
                    type="text"
                    placeholder="Поиск по названию, номеру..."
                    value={advancedFilters.searchTerm || ''}
                    onChange={(e) => handleApplyAdvancedFilters({ ...advancedFilters, searchTerm: e.target.value })}
                    className={styles.searchInput}
                />
            </div>
            <div className={styles.listContainer} ref={listRef}>
                {filteredFeatures.length > 0 ? (
                    filteredFeatures.map(feature => (
                        <div
                            key={feature.id}
                            data-id={feature.id}
                            className={`${styles.listItem} ${focusedFeatureId === feature.id ? styles.focused : ''}`}
                            onClick={() => onFeatureSelect(feature)}
                        >
                            <div className={styles.itemContent}>
                                <span className={styles.itemIcon} style={{ color: feature.properties.color || 'var(--color-text-secondary)' }}>{getIcon(feature.properties.type)}</span>
                                <div className={styles.itemInfo}>
                                <span className={styles.itemName}>{feature.properties.name || 'Без названия'}</span>
                                <span className={styles.itemSub}>{feature.properties.cadastral_number || `ID: ${feature.id.substring(0, 8)}...`}</span>
                                </div>
                            </div>
                            <label className={styles.visibilityToggle}>
                                <input
                                    type="checkbox"
                                    checked={feature.properties.visible !== false} // По умолчанию включен
                                    onChange={(e) => onVisibilityChange(feature.id, e.target.checked)}
                                    onClick={(e) => e.stopPropagation()} // Предотвращаем клик по всему элементу
                                />
                                <span className={styles.slider}></span>
                            </label>
                        </div>
                    ))
                ) : (
                    <p className={styles.noResults}>Объекты не найдены</p>
                )}
            </div>
            <div className={styles.footer}>
                <span>Найдено: {filteredFeatures.length}</span>
                <div className={styles.bulkActions}>
                    <button onClick={() => handleBulkVisibility(true)} className={styles.bulkButton} title="Добавить к видимым">
                        <PlusIcon size={16} />
                        <span>Показать</span>
                    </button>
                    <button onClick={() => handleBulkVisibility(false)} className={styles.bulkButton} title="Убрать из видимых">
                        <MinusIcon size={16} />
                        <span>Скрыть</span>
                    </button>
                </div>
            </div>

            <ObjectFilterPanel
                isVisible={isFilterPanelVisible}
                onClose={() => setIsFilterPanelVisible(false)}
                onApplyFilters={handleApplyAdvancedFilters}
                initialFilters={Object.keys(advancedFilters).length > 0 ? advancedFilters : defaultAdvancedFilters}
                allFeatures={mapData?.features}
                legendData={legendData}
            />
        </div>
    );
};

export default ObjectManagerPanel;